"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  formatRatio,
  formatScientific,
  parseScientificInput,
} from "@/lib/tool-kit/earth-science/format";
import {
  amplitudeRatio,
  DEFAULT_KM_PER_SP_SECOND,
  DYNE_CM_PER_NEWTON_METRE,
  distanceFromSpLag,
  energyJoules,
  energyJoulesFromMw,
  energyRatio,
  joulesToTntTons,
  LOCAL_MAGNITUDE_RANGE_KM,
  localMagnitude,
  momentFromFault,
  momentMagnitude,
  seismicMoment,
} from "@/lib/tool-kit/earth-science/seismology";

/** English strings. A translated content file overrides these through `ui`. */
export const EARTHQUAKE_MAGNITUDE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeCompare: "Compare two earthquakes",
  modeMoment: "Moment magnitude (Mw)",
  modeLocal: "Local magnitude (ML)",
  magnitudeA: "Earthquake A — magnitude",
  magnitudeB: "Earthquake B — magnitude",
  nameA: "A",
  nameB: "B",
  results: "Results",
  amplitudeRatio: "Ground-motion amplitude ratio",
  energyRatio: "Energy ratio",
  magnitudeGap: "Magnitude difference",
  compareSummary:
    "Earthquake {big} has {amp} times the seismogram amplitude of earthquake {small} and releases about {energy} times as much energy.",
  compareEqual: "The magnitudes are equal, so both ratios are 1.",
  energyHeading: "Radiated energy of each earthquake",
  columnQuake: "Earthquake",
  columnMagnitude: "Magnitude",
  columnEnergy: "Energy (joules)",
  columnTnt: "Roughly equal to",
  tntTons: "{value} tons of TNT",
  tntKilotons: "{value} kilotons of TNT",
  tntMegatons: "{value} megatons of TNT",
  compareNote:
    "Energy uses log₁₀E = 1.5M + 4.8 (E in joules), the Gutenberg–Richter relation. It is an estimate of radiated seismic energy, not of the damage an earthquake does.",
  momentModeLabel: "Direction",
  momentToMw: "Seismic moment → Mw",
  mwToMoment: "Mw → seismic moment",
  faultToMoment: "Fault size → moment and Mw",
  momentLabel: "Seismic moment M₀",
  momentHint: "Plain or exponent form: 4e19 or 4 × 10^19.",
  momentUnit: "Unit of M₀",
  unitNewtonMetre: "newton-metres (N·m)",
  unitDyneCm: "dyne-centimetres (dyne·cm)",
  mwLabel: "Moment magnitude Mw",
  rigidityLabel: "Rigidity μ (GPa)",
  rigidityHint: "About 30 GPa for typical crust.",
  lengthLabel: "Rupture length (km)",
  widthLabel: "Rupture width (km)",
  slipLabel: "Average slip (m)",
  resultMw: "Moment magnitude Mw",
  resultMoment: "Seismic moment M₀ (N·m)",
  resultMomentDyne: "Seismic moment M₀ (dyne·cm)",
  resultEnergy: "Radiated energy (joules)",
  resultEnergyUsgs: "Energy by USGS Mw estimate (joules)",
  resultArea: "Rupture area (km²)",
  momentNote:
    "Mw = ⅔ (log₁₀M₀ − 9.1) with M₀ in newton-metres — Hanks and Kanamori's moment magnitude scale, with the constant USGS publishes. For dyne·cm the same formula reads Mw = ⅔ log₁₀M₀ − 10.7.",
  amplitudeLabel: "Peak amplitude A (mm)",
  amplitudeHint: "Wood-Anderson seismograph trace, peak (not peak-to-peak).",
  distanceKindLabel: "How far away was the earthquake?",
  distanceKnown: "I know the distance",
  distanceFromSp: "I know the S−P time",
  distanceLabel: "Distance r (km)",
  distanceHint: "Hypocentral distance — straight-line from the focus.",
  spLabel: "S−P time (seconds)",
  spHint: "Gap between the first P wave and the first S wave.",
  kmPerSecondLabel: "Kilometres per second of S−P time",
  kmPerSecondHint: "About 8 for most crustal earthquakes.",
  resultMl: "Local magnitude ML",
  resultCorrection: "Distance correction −log A₀",
  resultDistance: "Distance used (km)",
  stepsHeading: "The working",
  stepDistance: "Distance: r = {lag} s × {rate} km/s = {distance} km.",
  stepAmplitude: "log₁₀A = log₁₀({amplitude} mm) = {value}.",
  stepCorrection:
    "−log₁₀A₀(r) = 1.110 log₁₀({distance}/100) + 0.00189 ({distance} − 100) + 3.0 = {value}.",
  stepMagnitude: "ML = {logA} + {correction} = {value}.",
  rangeWarning:
    "A distance of {distance} km is outside the 10–700 km Hutton and Boore used to fit this formula. Treat the result as a rough guide.",
  localNote:
    "This is the Southern California calibration of the Richter scale (Hutton and Boore, 1987). Other regions use their own distance corrections, and the amplitude must come from a Wood-Anderson response.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyCompare:
    "Magnitude {a} vs {b}: amplitude ratio {amp}, energy ratio {energy}",
  copyMoment: "M0 = {moment} N·m, Mw = {mw}",
  copyLocal: "ML = {ml} (A = {amplitude} mm, r = {distance} km)",
  enterValues: "Enter the values above to see the result.",
  invalidNumber: "One of the entries is not a number: {name}.",
  needPositive: "{name} must be greater than zero.",
  magnitudeRange:
    "Magnitudes between −2 and 10 are meaningful; the largest recorded earthquake is about Mw 9.5.",
  distanceNeeded: "Enter a distance or an S−P time above zero.",
} as const;

type Strings = typeof EARTHQUAKE_MAGNITUDE_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "compare" | "moment" | "local";
type MomentMode = "toMw" | "toMoment" | "fault";
type DistanceKind = "known" | "sp";

const MAGNITUDE_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 2 };
const MIN_MAGNITUDE = -2;
const MAX_MAGNITUDE = 10;

function magnitudeText(fmt: Fmt, value: number): string {
  return fmt(value, MAGNITUDE_FORMAT);
}

function tntText(strings: Strings, fmt: Fmt, joules: number): string {
  const tons = joulesToTntTons(joules);
  if (tons >= 1e6) {
    return t(strings.tntMegatons, { value: formatRatio(fmt, tons / 1e6) });
  }
  if (tons >= 1e3) {
    return t(strings.tntKilotons, { value: formatRatio(fmt, tons / 1e3) });
  }
  return t(strings.tntTons, { value: formatRatio(fmt, tons) });
}

function CompareResult({
  a,
  b,
  strings,
  fmt,
}: {
  a: number;
  b: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const gap = Math.abs(a - b);
  const amp = amplitudeRatio(gap);
  const energy = energyRatio(gap);
  const bigIsA = a >= b;
  const rows = [
    [strings.nameA, magnitudeText(fmt, a), energyJoules(a)],
    [strings.nameB, magnitudeText(fmt, b), energyJoules(b)],
  ] as const;
  return (
    <>
      <StatGrid>
        <StatCard
          label={strings.magnitudeGap}
          value={magnitudeText(fmt, gap)}
        />
        <StatCard
          emphasis
          label={strings.amplitudeRatio}
          value={formatRatio(fmt, amp)}
        />
        <StatCard
          emphasis
          label={strings.energyRatio}
          value={formatRatio(fmt, energy)}
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        {gap === 0
          ? strings.compareEqual
          : t(strings.compareSummary, {
              big: bigIsA ? strings.nameA : strings.nameB,
              small: bigIsA ? strings.nameB : strings.nameA,
              amp: formatRatio(fmt, amp),
              energy: formatRatio(fmt, energy),
            })}
      </p>
      <h3 className={WIDGET_H3}>{strings.energyHeading}</h3>
      <DataTable
        head={[
          strings.columnQuake,
          strings.columnMagnitude,
          strings.columnEnergy,
          strings.columnTnt,
        ]}
        rows={rows.map(([name, magnitude, joules]) => [
          name,
          magnitude,
          formatScientific(fmt, joules),
          tntText(strings, fmt, joules),
        ])}
      />
      <p className={HINT}>{strings.compareNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyCompare, {
          a: magnitudeText(fmt, a),
          b: magnitudeText(fmt, b),
          amp: formatRatio(fmt, amp),
          energy: formatRatio(fmt, energy),
        })}
      />
    </>
  );
}

function MomentResult({
  momentNm,
  strings,
  fmt,
  area,
}: {
  momentNm: number;
  strings: Strings;
  fmt: Fmt;
  area: number | null;
}) {
  const mw = momentMagnitude(momentNm);
  if (mw === null) {
    return (
      <ErrorNotice>
        {t(strings.needPositive, { name: strings.momentLabel })}
      </ErrorNotice>
    );
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultMw}
          value={magnitudeText(fmt, mw)}
        />
        <StatCard
          label={strings.resultMoment}
          value={formatScientific(fmt, momentNm)}
        />
        <StatCard
          label={strings.resultMomentDyne}
          value={formatScientific(fmt, momentNm * DYNE_CM_PER_NEWTON_METRE)}
        />
        <StatCard
          label={strings.resultEnergy}
          value={formatScientific(fmt, energyJoules(mw))}
        />
        <StatCard
          label={strings.resultEnergyUsgs}
          value={formatScientific(fmt, energyJoulesFromMw(mw))}
        />
        {area === null ? null : (
          <StatCard
            label={strings.resultArea}
            value={fmt(area, { maximumFractionDigits: 1 })}
          />
        )}
      </StatGrid>
      <p className={HINT}>{strings.momentNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyMoment, {
          moment: formatScientific(fmt, momentNm),
          mw: magnitudeText(fmt, mw),
        })}
      />
    </>
  );
}

function LocalResult({
  amplitude,
  distance,
  lag,
  rate,
  strings,
  fmt,
}: {
  amplitude: number;
  distance: number;
  lag: number | null;
  rate: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const result = localMagnitude(amplitude, distance);
  if (result === null) {
    return <ErrorNotice>{strings.distanceNeeded}</ErrorNotice>;
  }
  const outside =
    distance < LOCAL_MAGNITUDE_RANGE_KM.min ||
    distance > LOCAL_MAGNITUDE_RANGE_KM.max;
  const three = { maximumFractionDigits: 3 };
  const steps = [
    ...(lag === null
      ? []
      : [
          t(strings.stepDistance, {
            lag: fmt(lag, three),
            rate: fmt(rate, three),
            distance: fmt(distance, three),
          }),
        ]),
    t(strings.stepAmplitude, {
      amplitude: fmt(amplitude, three),
      value: fmt(result.logAmplitude, three),
    }),
    t(strings.stepCorrection, {
      distance: fmt(distance, three),
      value: fmt(result.correction, three),
    }),
    t(strings.stepMagnitude, {
      logA: fmt(result.logAmplitude, three),
      correction: fmt(result.correction, three),
      value: magnitudeText(fmt, result.magnitude),
    }),
  ];
  return (
    <>
      {outside ? (
        <Notice>
          {t(strings.rangeWarning, { distance: fmt(distance, three) })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultMl}
          value={magnitudeText(fmt, result.magnitude)}
        />
        <StatCard
          label={strings.resultCorrection}
          value={fmt(result.correction, { maximumFractionDigits: 3 })}
        />
        <StatCard
          label={strings.resultDistance}
          value={fmt(distance, { maximumFractionDigits: 1 })}
        />
      </StatGrid>
      <Panel title={strings.stepsHeading}>
        <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Panel>
      <p className={HINT}>{strings.localNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLocal, {
          ml: magnitudeText(fmt, result.magnitude),
          amplitude: fmt(amplitude, three),
          distance: fmt(distance, three),
        })}
      />
    </>
  );
}

function FaultResult({
  rigidity,
  length,
  width,
  slip,
  strings,
  fmt,
}: {
  rigidity: string;
  length: string;
  width: string;
  slip: string;
  strings: Strings;
  fmt: Fmt;
}) {
  const mu = parseScientificInput(rigidity);
  const len = parseScientificInput(length);
  const wid = parseScientificInput(width);
  const dis = parseScientificInput(slip);
  if (mu === null || len === null || wid === null || dis === null) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  if (mu <= 0 || len <= 0 || wid <= 0 || dis <= 0) {
    return (
      <ErrorNotice>
        {t(strings.needPositive, { name: strings.rigidityLabel })}
      </ErrorNotice>
    );
  }
  const areaKm2 = len * wid;
  return (
    <MomentResult
      area={areaKm2}
      fmt={fmt}
      momentNm={momentFromFault(mu * 1e9, areaKm2 * 1e6, dis)}
      strings={strings}
    />
  );
}

function magnitudeProblem(values: number[], strings: Strings): string | null {
  if (values.some((value) => value < MIN_MAGNITUDE || value > MAX_MAGNITUDE)) {
    return strings.magnitudeRange;
  }
  return null;
}

export default function EarthquakeMagnitudeCalculatorWidget() {
  const strings = useToolUi(EARTHQUAKE_MAGNITUDE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("compare");
  const [magA, setMagA] = useState("7.0");
  const [magB, setMagB] = useState("5.0");
  const [momentMode, setMomentMode] = useState<MomentMode>("toMw");
  const [moment, setMoment] = useState("4e19");
  const [momentUnit, setMomentUnit] = useState<"nm" | "dyne">("nm");
  const [mwText, setMwText] = useState("7.0");
  const [rigidity, setRigidity] = useState("30");
  const [length, setLength] = useState("100");
  const [width, setWidth] = useState("20");
  const [slip, setSlip] = useState("2");
  const [amplitude, setAmplitude] = useState("12");
  const [distanceKind, setDistanceKind] = useState<DistanceKind>("known");
  const [distanceText, setDistanceText] = useState("85");
  const [spText, setSpText] = useState("10");
  const [rateText, setRateText] = useState(String(DEFAULT_KM_PER_SP_SECOND));

  function compare() {
    const a = parseScientificInput(magA);
    const b = parseScientificInput(magB);
    if (a === null || b === null) {
      return <Notice>{strings.enterValues}</Notice>;
    }
    const problem = magnitudeProblem([a, b], strings);
    if (problem) {
      return <ErrorNotice>{problem}</ErrorNotice>;
    }
    return <CompareResult a={a} b={b} fmt={fmt} strings={strings} />;
  }

  function momentPanel() {
    if (momentMode === "toMw") {
      const m0 = parseScientificInput(moment);
      if (m0 === null) {
        return <Notice>{strings.enterValues}</Notice>;
      }
      const nm = momentUnit === "nm" ? m0 : m0 / DYNE_CM_PER_NEWTON_METRE;
      return (
        <MomentResult area={null} fmt={fmt} momentNm={nm} strings={strings} />
      );
    }
    if (momentMode === "toMoment") {
      const mw = parseScientificInput(mwText);
      if (mw === null) {
        return <Notice>{strings.enterValues}</Notice>;
      }
      const problem = magnitudeProblem([mw], strings);
      if (problem) {
        return <ErrorNotice>{problem}</ErrorNotice>;
      }
      return (
        <MomentResult
          area={null}
          fmt={fmt}
          momentNm={seismicMoment(mw)}
          strings={strings}
        />
      );
    }
    return (
      <FaultResult
        fmt={fmt}
        length={length}
        rigidity={rigidity}
        slip={slip}
        strings={strings}
        width={width}
      />
    );
  }

  function local() {
    const amp = parseScientificInput(amplitude);
    const rate = parseScientificInput(rateText);
    const lag = distanceKind === "sp" ? parseScientificInput(spText) : null;
    const known = parseScientificInput(distanceText);
    const distance =
      distanceKind === "sp" && lag !== null && rate !== null
        ? distanceFromSpLag(lag, rate)
        : known;
    if (
      amp === null ||
      distance === null ||
      (distanceKind === "sp" && (lag === null || rate === null))
    ) {
      return <Notice>{strings.enterValues}</Notice>;
    }
    if (amp <= 0) {
      return (
        <ErrorNotice>
          {t(strings.needPositive, { name: strings.amplitudeLabel })}
        </ErrorNotice>
      );
    }
    return (
      <LocalResult
        amplitude={amp}
        distance={distance}
        fmt={fmt}
        lag={distanceKind === "sp" ? lag : null}
        rate={rate ?? DEFAULT_KM_PER_SP_SECOND}
        strings={strings}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="eqm-mode"
        onChange={setMode}
        options={[
          { value: "compare", label: strings.modeCompare },
          { value: "moment", label: strings.modeMoment },
          { value: "local", label: strings.modeLocal },
        ]}
        value={mode}
      />
      {mode === "compare" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="eqm-mag-a"
            label={strings.magnitudeA}
            numeric
            onChange={setMagA}
            value={magA}
          />
          <TextField
            id="eqm-mag-b"
            label={strings.magnitudeB}
            numeric
            onChange={setMagB}
            value={magB}
          />
        </div>
      ) : null}
      {mode === "moment" ? (
        <div className="space-y-4">
          <PillGroup
            label={strings.momentModeLabel}
            name="eqm-moment-mode"
            onChange={setMomentMode}
            options={[
              { value: "toMw", label: strings.momentToMw },
              { value: "toMoment", label: strings.mwToMoment },
              { value: "fault", label: strings.faultToMoment },
            ]}
            value={momentMode}
          />
          {momentMode === "toMw" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                hint={strings.momentHint}
                id="eqm-moment"
                label={strings.momentLabel}
                numeric
                onChange={setMoment}
                value={moment}
              />
              <SelectField
                id="eqm-moment-unit"
                label={strings.momentUnit}
                onChange={setMomentUnit}
                options={[
                  { value: "nm", label: strings.unitNewtonMetre },
                  { value: "dyne", label: strings.unitDyneCm },
                ]}
                value={momentUnit}
              />
            </div>
          ) : null}
          {momentMode === "toMoment" ? (
            <TextField
              id="eqm-mw"
              label={strings.mwLabel}
              numeric
              onChange={setMwText}
              value={mwText}
            />
          ) : null}
          {momentMode === "fault" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                hint={strings.rigidityHint}
                id="eqm-rigidity"
                label={strings.rigidityLabel}
                numeric
                onChange={setRigidity}
                value={rigidity}
              />
              <TextField
                id="eqm-slip"
                label={strings.slipLabel}
                numeric
                onChange={setSlip}
                value={slip}
              />
              <TextField
                id="eqm-length"
                label={strings.lengthLabel}
                numeric
                onChange={setLength}
                value={length}
              />
              <TextField
                id="eqm-width"
                label={strings.widthLabel}
                numeric
                onChange={setWidth}
                value={width}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {mode === "local" ? (
        <div className="space-y-4">
          <TextField
            hint={strings.amplitudeHint}
            id="eqm-amplitude"
            label={strings.amplitudeLabel}
            numeric
            onChange={setAmplitude}
            value={amplitude}
          />
          <PillGroup
            label={strings.distanceKindLabel}
            name="eqm-distance-kind"
            onChange={setDistanceKind}
            options={[
              { value: "known", label: strings.distanceKnown },
              { value: "sp", label: strings.distanceFromSp },
            ]}
            value={distanceKind}
          />
          {distanceKind === "known" ? (
            <TextField
              hint={strings.distanceHint}
              id="eqm-distance"
              label={strings.distanceLabel}
              numeric
              onChange={setDistanceText}
              value={distanceText}
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                hint={strings.spHint}
                id="eqm-sp"
                label={strings.spLabel}
                numeric
                onChange={setSpText}
                value={spText}
              />
              <TextField
                hint={strings.kmPerSecondHint}
                id="eqm-rate"
                label={strings.kmPerSecondLabel}
                numeric
                onChange={setRateText}
                value={rateText}
              />
            </div>
          )}
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "compare" ? compare() : null}
        {mode === "moment" ? momentPanel() : null}
        {mode === "local" ? local() : null}
      </section>
    </div>
  );
}
